import React, { useState, useCallback, useEffect } from 'react'

import {
  Button,
  Card,
  Rating,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  number, string, bool,
} from 'prop-types'

import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import UserAvatar from 'Pods/Shared/UserAvatar/UserAvatar'

import slugify from 'Utils/slugify'

let NewContactModal

function SellerArea({
  designerId,
  designerName,
  avatarUrl = null,
  rating,
  reviewsCount,
  availableForHire,
  isFollowing: initialIsFollowing = false,
  extractingCoreStyles = false,
}) {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing)
  const [contactModalIsOpen, setContactModalIsOpen] = useState(false)
  const [isContactModalLoaded, setIsContactModalLoaded] = useState(false)

  const { isPreview } = usePreviewMode()
  const pageConfig = usePageConfig()
  const { isCurrentUser: isLoggedIn } = pageConfig || {}

  const { captureException } = useSentry()

  const {
    postData,
    isLoading: isPostDataLoading,
  } = useApiPost()

  const {
    deleteData,
    isLoading: isDeleteDataLoading,
  } = useApiDelete()

  const isLoading = isPostDataLoading || isDeleteDataLoading
  const profileLink = `/designers/${slugify(designerName)}`
  const reviewsLink = `${profileLink}#reviews`

  useEffect(() => {
    const { product } = pageConfig || {}
    if (!product) {
      return
    }

    if (!extractingCoreStyles) {
      setIsFollowing(product.following || false)
    }
    setIsInitialized(true)
  }, [pageConfig])

  useEffect(() => {
    if (contactModalIsOpen && !NewContactModal && !isContactModalLoaded) {
      importNewContactModal()
    }
  }, [contactModalIsOpen, isContactModalLoaded])

  function importNewContactModal() {
    import('Pods/LazyLoaded/NewContactModal/NewContactModal')
      .then((module) => {
        NewContactModal = module.default
        setIsContactModalLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load NewContactModal:', error)
      })
  }

  function handleMouseEnter() {
    if (!isContactModalLoaded && !NewContactModal) {
      importNewContactModal()
    }
  }

  function handleTouchStart() {
    if (!isContactModalLoaded && !NewContactModal) {
      importNewContactModal()
    }
  }

  const handleFollowUnfollowClick = useCallback((event) => {
    event.preventDefault()

    if (!isLoggedIn) {
      App.events.publish('app:login:open')

      return
    }

    if (!isFollowing) {
      postData({
        path: `/api/internal/users/${designerId}/follow`,
        onSuccess: () => {
          setIsFollowing(true)
          window.showNotification(
            'You are now following this designer!',
            {
              type: 'success',
              autohide: true,
            }
          )
        },
        onError: (err) => {
          window.showNotification(
            'Failed to follow designer',
            {
              type: 'error',
              autohide: true,
            }
          )
          captureException(err)
        },
      })

      return
    }

    deleteData({
      path: `/api/internal/users/${designerId}/unfollow`,
      onSuccess: () => {
        setIsFollowing(false)
        window.showNotification(
          'You are no longer following this designer!',
          {
            type: 'success',
            autohide: true,
          }
        )
      },
      onError: (err) => {
        window.showNotification(
          'Failed to unfollow designer',
          {
            type: 'error',
            autohide: true,
          }
        )
        captureException(err)
      },
    })
  }, [isFollowing, designerId, isLoggedIn])

  const handleOpenMessageModal = () => {
    if (!isLoggedIn) {
      App.events.publish('app:login:open')
      return
    }

    setContactModalIsOpen(true)

    if (!NewContactModal && !isContactModalLoaded) {
      importNewContactModal()
    }
  }

  const handleCloseMessageModal = () => {
    setContactModalIsOpen(false)
  }

  return (
    <Card hasBorder isRounded customClass="seller-area">
      <div className="seller-area__header">
        <span className="seller-area__header-label">
          Created by
        </span>
        <div className="seller-area__header-wrapper">
          <a href={profileLink}>
            <UserAvatar
              img={avatarUrl}
              userName={designerName}
              className="seller-area__avatar"
              width="56"
              height="48"
            />
          </a>
          <div className="seller-area__header-content">
            <div className="seller-area__header-content-wrapper">
              <a href={profileLink} className="seller-area__header-content-username">{designerName}</a>
            </div>
            <div className="seller-area__header-content-rating">
              <a
                href={reviewsLink}
                className="seller-area__rating-link"
              >
                <Rating
                  id="seller-area-rating"
                  initialValue={rating}
                  numberOfItems={5}
                  size="small"
                  trail={reviewsCount === 1 ? `(1 review)` : `(${reviewsCount} reviews)`}
                  isCheckboxHidden
                  className="seller-area__rating"
                  ariaLabelPrefix={`${designerName} rating`}
                  isFractional
                />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="seller-area__actions">
        {availableForHire && (
          <Button
            href={`/3d-modeling-jobs/new/hire-designer/${designerName}?meta_source=product_page`}
            expand
            type="secondary"
            customClass="seller-area__hire-button"
            disabled={isPreview || !isInitialized || isLoading}
            aria-label="Hire"
          >
            Hire
          </Button>
        )}
        <Button
          onClick={handleOpenMessageModal}
          onMouseEnter={handleMouseEnter}
          onTouchStart={handleTouchStart}
          expand
          type="secondary"
          customClass="seller-area__message-button"
          disabled={isPreview}
          aria-label="Message the designer"
        >
          Message
        </Button>
        <Button
          onClick={handleFollowUnfollowClick}
          expand
          type="secondary"
          customClass={isFollowing ? 'seller-area__solo-button' : 'seller-area__follow-button'}
          disabled={isPreview || !isInitialized || isLoading}
          aria-label={isFollowing ? 'Following' : 'Follow'}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </Button>
      </div>

      {contactModalIsOpen && isContactModalLoaded && NewContactModal && (
        <NewContactModal
          closeModal={handleCloseMessageModal}
          itemId={pageConfig?.product?.id}
          authorUsername={designerName}
          conversationType="personal"
          headerText="Message the designer"
        />
      )}
    </Card>
  )
}

SellerArea.propTypes = {
  designerId: number.isRequired,
  designerName: string.isRequired,
  avatarUrl: string,
  rating: number.isRequired,
  reviewsCount: number.isRequired,
  availableForHire: bool.isRequired,
  isFollowing: bool,
  extractingCoreStyles: bool,
}

export default SellerArea
